/**
 * Affordability Calculator Block
 * "How much can you borrow?" calculator based on income.
 */

import { registerBlockRenderer, getBlockRendererContext } from '../registry';
import { escapeHtml, createBlockWrapper } from '../utils';

// =============================================================================
// TYPES
// =============================================================================

interface AffordabilityData {
  income?: number;
  partnerIncome?: number;
  deposit?: number;
  title?: string;
  multiplier?: number;
  currency?: string;
}

// =============================================================================
// TYPE VALIDATION
// =============================================================================

function isValidAffordabilityData(data: unknown): data is AffordabilityData {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;

  if (d.income !== undefined && typeof d.income !== 'number') return false;
  if (d.partnerIncome !== undefined && typeof d.partnerIncome !== 'number') return false;
  if (d.deposit !== undefined && typeof d.deposit !== 'number') return false;
  if (d.multiplier !== undefined && typeof d.multiplier !== 'number') return false;
  if (d.title !== undefined && typeof d.title !== 'string') return false;

  return true;
}

// =============================================================================
// RENDER FUNCTION
// =============================================================================

function renderAffordabilityCalc(data: AffordabilityData, container: HTMLElement): void {
  const wrapper = createBlockWrapper('affordability-calc');

  // Get currency from data, fall back to context, then to £
  const context = getBlockRendererContext();
  const currency = data.currency || context.currencySymbol || '£';
  const defaultIncome = data.income || 50000;
  const defaultPartnerIncome = data.partnerIncome || 0;
  const defaultDeposit = data.deposit || 25000;
  const multiplier = data.multiplier || 4.5;

  let html = `<h3 class="pms-affordability-title">${escapeHtml(data.title || 'How Much Can You Borrow?')}</h3>`;
  html += '<div class="pms-affordability-calc">';

  // Income inputs
  html += `
    <div class="pms-affordability-fields">
      <div class="pms-affordability-field">
        <label class="pms-affordability-label">Your Annual Income</label>
        <div class="pms-affordability-input-group">
          <span class="pms-affordability-currency">${escapeHtml(currency)}</span>
          <input type="number" class="pms-affordability-input" data-field="income1" value="${defaultIncome}" min="0" max="1000000" step="1000">
        </div>
      </div>
      <div class="pms-affordability-field">
        <label class="pms-affordability-label">Partner's Income (optional)</label>
        <div class="pms-affordability-input-group">
          <span class="pms-affordability-currency">${escapeHtml(currency)}</span>
          <input type="number" class="pms-affordability-input" data-field="income2" value="${defaultPartnerIncome}" min="0" max="1000000" step="1000">
        </div>
      </div>
    </div>
  `;

  // Deposit
  html += `
    <div class="pms-affordability-field">
      <label class="pms-affordability-label">Your Deposit</label>
      <div class="pms-affordability-input-group">
        <span class="pms-affordability-currency">${escapeHtml(currency)}</span>
        <input type="number" class="pms-affordability-input" data-field="deposit" value="${defaultDeposit}" min="0" max="5000000" step="1000">
      </div>
    </div>
  `;

  // Results
  html += `
    <div class="pms-affordability-results">
      <div class="pms-affordability-result-main">
        <span class="pms-affordability-result-label">You could borrow up to</span>
        <span class="pms-affordability-result-value" data-display="borrow">${currency}0</span>
        <span class="pms-affordability-result-note">Based on ${multiplier}x income multiplier</span>
      </div>
      <div class="pms-affordability-meter">
        <div class="pms-affordability-meter-label">
          <span>Borrowing Power</span>
          <span data-display="percent">0%</span>
        </div>
        <div class="pms-affordability-meter-bar">
          <div class="pms-affordability-meter-fill" data-display="meter"></div>
        </div>
      </div>
      <div class="pms-affordability-details">
        <div class="pms-affordability-detail">
          <span class="pms-affordability-detail-label">Property Budget</span>
          <span class="pms-affordability-detail-value" data-display="property">${currency}0</span>
        </div>
        <div class="pms-affordability-detail">
          <span class="pms-affordability-detail-label">Combined Income</span>
          <span class="pms-affordability-detail-value" data-display="totalIncome">${currency}0</span>
        </div>
      </div>
    </div>
  `;

  html += '</div>';
  wrapper.innerHTML = html;

  // Get elements
  const income1Input = wrapper.querySelector('[data-field="income1"]') as HTMLInputElement;
  const income2Input = wrapper.querySelector('[data-field="income2"]') as HTMLInputElement;
  const depositInput = wrapper.querySelector('[data-field="deposit"]') as HTMLInputElement;
  const borrowDisplay = wrapper.querySelector('[data-display="borrow"]') as HTMLElement;
  const propertyDisplay = wrapper.querySelector('[data-display="property"]') as HTMLElement;
  const totalIncomeDisplay = wrapper.querySelector('[data-display="totalIncome"]') as HTMLElement;
  const meterFill = wrapper.querySelector('[data-display="meter"]') as HTMLElement;
  const percentDisplay = wrapper.querySelector('[data-display="percent"]') as HTMLElement;

  function formatCurrency(value: number): string {
    return currency + Math.round(value).toLocaleString();
  }

  function calculate(): void {
    const income1 = parseFloat(income1Input.value) || 0;
    const income2 = parseFloat(income2Input.value) || 0;
    const deposit = parseFloat(depositInput.value) || 0;

    const totalIncome = income1 + income2;
    const maxBorrow = totalIncome * multiplier;
    const propertyBudget = maxBorrow + deposit;

    borrowDisplay.textContent = formatCurrency(maxBorrow);
    propertyDisplay.textContent = formatCurrency(propertyBudget);
    totalIncomeDisplay.textContent = formatCurrency(totalIncome);

    // Meter (0-1M scale)
    const meterPercent = Math.min((maxBorrow / 1000000) * 100, 100);
    meterFill.style.width = `${meterPercent}%`;
    percentDisplay.textContent = `${Math.round(meterPercent)}%`;
  }

  // Event listeners
  income1Input.addEventListener('input', calculate);
  income2Input.addEventListener('input', calculate);
  depositInput.addEventListener('input', calculate);

  // Initial calculation
  calculate();

  container.appendChild(wrapper);
}

// =============================================================================
// REGISTRATION
// =============================================================================

registerBlockRenderer('affordability-calc', (data: unknown, container: HTMLElement) => {
  if (!isValidAffordabilityData(data)) {
    console.warn('[affordability-calc] Invalid data:', data);
    const wrapper = createBlockWrapper('affordability-calc');
    wrapper.innerHTML = '<div class="pms-block-error">Invalid affordability calculator data</div>';
    container.appendChild(wrapper);
    return;
  }

  renderAffordabilityCalc(data, container);
});
